import { useQuery } from '@tanstack/react-query';
import { formatISO } from 'date-fns';
import backendApi from '../services/axios.auth.wrapper';

const baseURL = process.env.REACT_APP_BACKEND_URL;

type DateRange = { startDate?: Date | null; endDate?: Date | null } | null | undefined;

const serializeArray = (values: (string | number)[] | null | undefined): string => 
  values?.join('|') ?? '';

const serializeDateRange = (dateRange: DateRange): string => {
  if (!dateRange?.startDate || !dateRange?.endDate) return '';
  return `${formatISO(dateRange.startDate, { representation: 'date' })}_${formatISO(dateRange.endDate, { representation: 'date' })}`;
};

const DEFAULT_QUERY_OPTIONS = {
  staleTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  keepPreviousData: true,
};

export const GetLastProcessedDetection = (site_id?: string) =>
  useQuery(
    ['last-processed-detection', site_id ?? ''],
    () => {
      return backendApi({
        method: 'get',
        url: `${baseURL}/api/segments/latest`,
        params: site_id ? { site_id } : undefined,
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id },
  );

export const GetBirdsDetectedAtSite = (
  site_id: string,
  camera_ids: number[],
  species_order: string[] = [],
  species_name: string[] = [],
) =>
  useQuery(
    ['birds-detected-at-site', site_id, serializeArray(camera_ids), serializeArray(species_order), serializeArray(species_name)],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/birds_detected_at_site/`,
        data: {
          site_id,
          camera_ids,
          species_order,
          species_name,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id && camera_ids?.length > 0 },
  );

export const GetBirdsDetectedAtSiteWithDateRange = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
  species_order: string[] = [],
  species_name: string[] = [],
) =>
  useQuery(
    [
      'birds-detected-at-site-with-date-range',
      serializeDateRange(dateRange),
      site_id,
      serializeArray(camera_ids),
      serializeArray(species_order),
      serializeArray(species_name),
    ],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/bird_count_date_range`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
          species_order,
          species_name,
          aggregation: 'daily',
        },
      });
    },
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate && camera_ids?.length > 0,
      select: (data: any) => {
        if (data?.data && Array.isArray(data.data)) {
          return data.data.reduce((total: number, entry: any) => {
            return total + (entry.number_of_birds || 0);
          }, 0);
        }
        return 0;
      },
    },
  );

export const GetDashboardStats = (site_id: string) =>
  useQuery(
    ['dashboard-stats', site_id],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/dashboard`,
        data: {
          site_id,
        },
      });
    },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!site_id },
  );

export const GetUniqueSpeciesCount = (
  dateRange: DateRange,
  site_id: string,
  camera_ids: number[],
) =>
  useQuery(
    ['unique-species-count', serializeDateRange(dateRange), site_id, serializeArray(camera_ids)],
    () => {
      return backendApi({
        method: 'post',
        url: `${baseURL}/api/species_list`,
        data: {
          start_date: formatISO(dateRange!.startDate!, {
            representation: 'date',
          }),
          end_date: formatISO(dateRange!.endDate!, {
            representation: 'date',
          }),
          site_id,
          camera_ids,
        },
      });
    },
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!site_id && !!dateRange?.startDate && !!dateRange?.endDate && camera_ids?.length > 0,
      select: (data: any) => {
        if (data?.data && Array.isArray(data.data)) {
          const uniqueSpecies = new Set();
          data.data.forEach((element: any) => {
            const speciesName = element.species_name__species_name;
            if (speciesName) {
              uniqueSpecies.add(speciesName);
            }
          });
          return uniqueSpecies.size;
        }
        return 0;
      },
    },
  );
